import { getTreatment } from '@eventbrite/splitio-client';

/**
 * Split.io feature flag names mapped to their application keys.
 *
 * To add a new flag:
 * 1. Add entry here: `myNewFlag: 'split_io_flag_name'`
 * 2. Add default in `DEFAULT_FEATURE_FLAGS`
 * 3. Add evaluation in `evaluateFeatureFlags`
 */
const FLAGS = {
  shareFlyer: 'enable_share_flyer',
  topOrganizerListingPage: 'top_organizer_listing_page',
} as const;

export type FeatureFlags = {
  [K in keyof typeof FLAGS]: boolean;
};

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  shareFlyer: false,
  topOrganizerListingPage: false,
};

/**
 * Context passed to feature flag evaluation.
 * Contains all attributes that may be used for flag targeting in Split.io.
 */
export interface FeatureFlagContext {
  eventId: string;
  organizationId?: string;
}

/**
 * Evaluates all feature flags for the given context.
 *
 * Each flag specifies which attributes it needs from the context.
 * Flags are evaluated in parallel.
 *
 * @param ctx - Context containing attributes for flag targeting
 * @returns Promise resolving to all feature flag values
 */
export async function evaluateFeatureFlags(ctx: FeatureFlagContext): Promise<FeatureFlags> {
  const flagEntries = await Promise.all(
    (Object.entries(FLAGS) as [keyof FeatureFlags, string][]).map(async ([flagKey, flagName]) => {
      const value = await evaluateFlag(flagName, ctx.eventId, {
        eventId: ctx.eventId,
        organization_id: ctx.organizationId,
      });
      return [flagKey, value] as const;
    }),
  );

  return flagEntries.reduce((acc, [flagKey, value]) => ({ ...acc, [flagKey]: value }), DEFAULT_FEATURE_FLAGS);
}

/**
 * Evaluates a single feature flag.
 *
 * @param flagName - Split.io flag name
 * @param key - Split.io key (typically eventId)
 * @param attributes - Targeting attributes
 * @returns `true` if treatment is 'on', `false` otherwise or on error
 */
async function evaluateFlag(
  flagName: string,
  key: string,
  attributes: Record<string, string | undefined>,
): Promise<boolean> {
  try {
    const cleanAttributes: Record<string, string> = {};
    for (const [attrKey, value] of Object.entries(attributes)) {
      if (value !== undefined) cleanAttributes[attrKey] = value;
    }
    const treatment = await getTreatment(key, flagName, cleanAttributes);
    return treatment === 'on';
  } catch (error) {
    console.warn(`Failed to evaluate feature flag: ${flagName}`, { error, key });
    const flagKey = (Object.entries(FLAGS) as [keyof FeatureFlags, string][]).find(
      ([, value]) => value === flagName,
    )?.[0];
    return (flagKey !== undefined ? DEFAULT_FEATURE_FLAGS[flagKey] : undefined) ?? false;
  }
}
